/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import req from '@sportninja/common/api/request'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import { getAllPlayerTypes } from '@sportninja/common/selectors/types'
import { useRef, useState } from 'react'
import { connect } from 'react-redux'

import { FormButton } from '../../Form/css'
import { Flex } from '../../Layout'
import Checkbox from '../../ts-components/Checkbox'
import ImportCSVErrorTable from '../ImportCSVErrorTable'
import ImportCSVBanner, { BANNER_TYPES } from '../ImportCSVBanner'
import ImportTeamRosterDuplicatePlayer from './ImportTeamRosterDuplicatePlayer'
import { useDuplicateDecisions } from './hooks/useDuplicateDecisions'
import {
  confirmUploadFile,
  DuplicateRowMismatchError,
} from './utils/applyDuplicateDecisions'
import { importCsvLayout, importRowLabels } from './utils/importCsvRows'
import { importRequestOutcome } from './utils/importRequestOutcome'
import { font } from '../../css'

const ImportTeamRosterErrorDuplicates = ({
  conflicts = [],
  playerTypes,
  teamName,
  resetFile,
  csvRows,
  file,
  submitUrl,
  onUploadComplete,
  onImportError,
  setLoading,
  isMultiTeamRoster = false,
}) => {
  'use memo'
  // Set before the request goes out, so a second submit event sends nothing.
  // Left set once the outcome is unknown: this review never posts again.
  const inFlight = useRef(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(false)
  const [outcomeUnknown, setOutcomeUnknown] = useState(false)
  const [shouldHighlightDiff, setShouldHighlightDiff] = useState(false)

  const {
    decisions,
    dispatch,
    createAllChecked,
    firstListedChecked,
    canFirstListedAll,
    ignoredCount,
    isComplete,
    setCreateAll,
    setFirstListedAll,
  } = useDuplicateDecisions(conflicts)

  const layout = importCsvLayout(csvRows)
  const dataRowCount = layout.dataRows.length

  const onSubmit = async (e) => {
    e.preventDefault()

    if (inFlight.current || !isComplete) {
      return
    }

    let upload
    try {
      upload = confirmUploadFile(file, csvRows, conflicts, decisions)
    } catch (rewriteError) {
      setError(
        rewriteError instanceof DuplicateRowMismatchError
          ? t('common:importDuplicatesRowMismatch', { line: rewriteError.line })
          : rewriteError.message
      )
      return
    }

    inFlight.current = true
    setError(false)
    setSubmitting(true)
    setLoading(true)

    const body = new FormData()
    body.append('file', upload)
    body.append('conflicts', 'ignore')

    try {
      const response = await req(submitUrl, {
        body,
        method: 'POST',
        skipAutoContentType: true,
        // One attempt: a replay after a lost response would create every new player again.
        maxAttempts: 1,
        reportGlobalError: false,
      })
      onUploadComplete(response)
    } catch (requestError) {
      const outcome = importRequestOutcome(requestError)

      if (outcome === 'unknown') {
        setOutcomeUnknown(true)
      } else {
        inFlight.current = false
        if (outcome === 'report') {
          onImportError(requestError)
        } else {
          setError(
            requestError?.message || 'There was a problem importing your file.'
          )
        }
      }
    } finally {
      setSubmitting(false)
      setLoading(false)
    }
  }

  return (
    <>
      <div role='status'>
        <ImportCSVBanner
          type={BANNER_TYPES.info.type}
          message={
            submitting
              ? t('common:importDuplicatesImporting', {
                  count: dataRowCount - ignoredCount,
                })
              : `${dataRowCount} players to import. Please review ${conflicts.length} potential duplicates below:`
          }
        />
      </div>
      {conflicts.length >= 2 && (
        <div css={bulkBoxesStyles}>
          <Checkbox
            id='import-duplicates-create-all'
            name='importDuplicatesCreateAll'
            label={t('common:importDuplicatesCreateAll', {
              count: conflicts.length,
            })}
            value={createAllChecked}
            onChange={(event) => setCreateAll(event.target.checked)}
            disabled={submitting}
          />
          {canFirstListedAll && (
            <Checkbox
              id='import-duplicates-first-listed-all'
              name='importDuplicatesFirstListedAll'
              label={t('common:importDuplicatesFirstListedAll', {
                count: conflicts.length,
              })}
              value={firstListedChecked}
              onChange={(event) => setFirstListedAll(event.target.checked)}
              disabled={submitting}
            />
          )}
        </div>
      )}
      <div role='status'>
        {firstListedChecked ? (
          <ImportCSVBanner
            type={BANNER_TYPES.warning.type}
            message={t('common:importDuplicatesFirstListedWarning')}
          />
        ) : null}
      </div>
      {outcomeUnknown ? (
        <ImportCSVBanner
          type={BANNER_TYPES.error.type}
          message={t('common:importDuplicatesOutcomeUnknown')}
        />
      ) : null}
      {error ? (
        <ImportCSVBanner type={BANNER_TYPES.error.type} message={error} />
      ) : null}
      <form
        id='team-roster-duplicates'
        action='/'
        method='GET'
        onSubmit={onSubmit}
        css={css`
          overflow: auto;
          margin-bottom: 16px;
          border-bottom-left-radius: 4px;
          border-bottom-right-radius: 4px;
        `}
      >
        <fieldset
          disabled={submitting}
          css={css`
            min-width: 0;
            margin: 0;
            padding: 0;
            border: 0;
          `}
        >
          <ImportCSVErrorTable
            wrapCss={rowDividerStyles}
            body={
              Array.isArray(conflicts) &&
              conflicts.map((player) => {
                const { line } = player
                const labels = importRowLabels(csvRows, layout, line)

                return (
                  <tr key={line} className='row'>
                    <td>
                      <ImportTeamRosterDuplicatePlayer
                        line={line}
                        player={player}
                        decision={decisions[line]}
                        dispatch={dispatch}
                        playerTypes={playerTypes}
                        teamName={teamName}
                        shouldHighlightDiff={shouldHighlightDiff}
                        importPlayerId={labels.snPlayerId}
                        importTeamName={
                          isMultiTeamRoster ? labels.teamName : null
                        }
                        importTeamRosterName={
                          isMultiTeamRoster ? labels.competitionName : null
                        }
                      />
                    </td>
                  </tr>
                )
              })
            }
          />
        </fieldset>
      </form>
      <Flex alignItems='center'>
        <button
          type='button'
          className={shouldHighlightDiff ? 'active' : ''}
          onClick={() => {
            setShouldHighlightDiff(!shouldHighlightDiff)
          }}
          css={css`
            display: flex;
            align-items: center;
            margin-bottom: 18px;

            span {
              margin-left: 10px;
              ${font.body}
              font-size: 16px;
              color: ${shouldHighlightDiff ? 'white' : '#9fa3a9'};
            }
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              width: 20px;
              height: 10px;
              border-radius: 5px;
              padding: 0 2px;
              background-color: ${shouldHighlightDiff
                ? colors.DEFAULT_FLAIR
                : '#9fa3a9'};

              .baton {
                background-color: #191b1f;
                width: 7px;
                height: 7px;
                border-radius: 50%;
                transition: transform 0.1s ease-in-out;
              }

              .active & {
                .baton {
                  transform: translateX(9px);
                }
              }
            `}
          >
            <div className='baton' />
          </div>
          <span>Highlight differences</span>
        </button>
      </Flex>
      <Flex alignItems='center' justifyContent='space-between'>
        <FormButton
          disabled={submitting}
          css={css`
            width: 160px;
            height: 40px;
            border: 2px solid ${colors.DEFAULT_FLAIR};
            background-image: none;
            color: white;

            &:hover {
              background-image: none !important;
            }
          `}
          onClick={resetFile}
        >
          Cancel {t('common:import')}
        </FormButton>
        <FormButton
          form='team-roster-duplicates'
          busy={submitting}
          disabled={submitting || outcomeUnknown || !isComplete}
          isSubmit
          css={css`
            width: 160px;
            height: 40px;
            background-color: ${colors.DEFAULT_FLAIR};
            background-image: none;

            &:hover {
              background-image: none !important;
            }
          `}
        >
          Confirm & {t('common:import')}
        </FormButton>
      </Flex>
    </>
  )
}

const mapStateToProps = (state) => {
  return {
    playerTypes: getAllPlayerTypes(state),
  }
}

export default connect(mapStateToProps)(ImportTeamRosterErrorDuplicates)

// ---- styles ----

const bulkBoxesStyles = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`

// One rule for every row: a style per <tr> was re-serialized for each of
// 1,000+ rows on every decision.
const rowDividerStyles = css`
  tbody tr:not(:last-child) {
    border-bottom: 1px solid ${colors.PALE_SKY};
  }
`
